import type {
  ChecklistAnswer,
  ChecklistResult,
  ChecklistResultInput,
  ChecklistResultType,
  CompletionIssue,
  Defect,
  DefectPriority,
  UserRef,
  WorkOrder,
  WorkOrderDetail,
  WorkOrderStatus,
} from '../api/types'
import type { OutboxRecord, OutboxState } from '../offline/db'
import { formatDate, formatNumber, MONTHS, plannedDate, plannedDateTimestamp } from '../utils/format'

export const MAX_PHOTO_BYTES = 20 * 1024 * 1024
/** Пауза перед отправкой изменённого пункта чек-листа. */
export const RESULT_SAVE_DELAY_MS = 600
/** Пауза перед записью оптимистичного состояния задания в офлайн-кэш. */
export const CACHE_PERSIST_DELAY_MS = 400

export const STARTABLE_STATUSES: readonly WorkOrderStatus[] = ['PLANNED', 'ASSIGNED', 'OVERDUE', 'REJECTED']
export const FINISHED_STATUSES: readonly WorkOrderStatus[] = ['COMPLETED', 'CANCELLED']

export function canStartWorkOrder(status?: WorkOrderStatus | null): boolean {
  return STARTABLE_STATUSES.includes(status ?? '')
}

export function isWorkOrderInProgress(status?: WorkOrderStatus | null): boolean {
  return status === 'IN_PROGRESS'
}

export function isWorkOrderFinished(status?: WorkOrderStatus | null): boolean {
  return FINISHED_STATUSES.includes(status ?? '')
}

/* ---------- Период ---------- */

export function monthKey(date: Date = new Date()): string {
  return plannedDate(date.getTime()).slice(0, 7)
}

export function isMonthKey(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
}

export function shiftMonth(key: string, delta: number): string {
  const [year, month] = key.split('-').map(Number)
  return monthKey(new Date(year, month - 1 + delta, 1))
}

export function monthTitle(key: string): string {
  const [year, month] = key.split('-').map(Number)
  return `${MONTHS[month - 1] ?? ''} ${year}`.trim()
}

export function workOrderCacheKey(id: string): string {
  return `work-order:${id}`
}

/**
 * Область списка: `mine` — только назначенные сотруднику; `all` — без фильтра по исполнителю.
 * Для исполнителя без прав планирования сервер сам ограничивает `all` его заданиями и свободными заданиями его объектов.
 */
export type WorkOrdersScope = 'mine' | 'all'

export function workOrdersCacheKey(month: string, scope: WorkOrdersScope | boolean): string {
  return scope === 'mine' || scope === true ? `work-orders:mine:${month}` : `work-orders:all:${month}`
}

export function workOrdersPath(month: string, scope: WorkOrdersScope | boolean): string {
  return `/work-orders?${scope === 'mine' || scope === true ? 'mine=1&' : ''}month=${month}&limit=200`
}

/* ---------- День исполнителя ---------- */

export function isDayKey(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(value)
}

export function dayKeyOf(date: Date = new Date()): string {
  return localDayKey(date)
}

export function shiftDay(key: string, delta: number): string {
  const [year, month, day] = key.split('-').map(Number)
  return localDayKey(new Date(year, month - 1, day + delta))
}

/** «Сегодня, 27 сентября», «Вчера, 26 сентября», «Пятница, 25 сентября» — заголовок дня в списке исполнителя. */
export function dayTitle(key: string, now: Date): string {
  const [year, month, day] = key.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  const diff = Math.round((date.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000)
  const weekday = new Intl.DateTimeFormat('ru-RU', { weekday: 'long' }).format(date)
  const prefix = diff === 0 ? 'Сегодня' : diff === 1 ? 'Завтра' : diff === -1 ? 'Вчера' : weekday.charAt(0).toUpperCase() + weekday.slice(1)
  return `${prefix}, ${new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(date)}`
}

export function workOrdersDayCacheKey(day: string): string {
  return `work-orders:day:${day}`
}

/** Задания одного дня: свои и свободные по объектам исполнителя (сервер ограничивает область сам, без флага mine). */
export function workOrdersDayPath(day: string): string {
  const from = new Date(plannedDateTimestamp(day))
  const to = new Date(from.getTime() + 86_400_000)
  return `/work-orders?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&limit=200`
}

export const OWN_OPEN_CACHE_KEY = 'work-orders:own-open'
/** Все открытые задания сотрудника независимо от дня: просроченные, начатые, возвращённые на доработку. */
export const OWN_OPEN_PATH = '/work-orders?mine=1&status=OPEN&limit=200'

/** Свободное задание объекта: без исполнителя, доступно для взятия в работу. */
export function isPoolWorkOrder(order: Pick<WorkOrder, 'assignee' | 'canClaim' | 'status'>): boolean {
  return !order.assignee && Boolean(order.canClaim) && !isWorkOrderFinished(order.status) && order.status !== 'IN_PROGRESS'
}

export function formatDuration(minutes?: number | null): string {
  if (!minutes || minutes <= 0) return '—'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (!hours) return `${rest} мин`
  return rest ? `${hours} ч ${rest} мин` : `${hours} ч`
}

function localDayKey(date: Date): string {
  return plannedDate(date.getTime())
}

export function dayKey(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : localDayKey(date)
}

export function dayLabel(value: string, now: Date): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Дата не указана'
  const formatted = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' }).format(date)
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diff = Math.round((target.getTime() - start.getTime()) / 86_400_000)
  if (diff === 0) return `Сегодня · ${formatted}`
  if (diff === 1) return `Завтра · ${formatted}`
  if (diff === -1) return `Вчера · ${formatted}`
  return formatted
}

/* ---------- Список заданий ---------- */

export type MyWorkSegment = 'open' | 'pool' | 'in_progress' | 'done' | 'colleagues' | 'all'

export const MY_WORK_SEGMENTS: ReadonlyArray<{ id: MyWorkSegment; label: string }> = [
  { id: 'open', label: 'К выполнению' },
  { id: 'pool', label: 'Свободные' },
  { id: 'in_progress', label: 'В работе' },
  { id: 'done', label: 'Завершённые' },
  { id: 'colleagues', label: 'У коллег' },
  { id: 'all', label: 'Все' },
]

export function parseSegment(value: string | null | undefined): MyWorkSegment {
  return MY_WORK_SEGMENTS.some((segment) => segment.id === value) ? (value as MyWorkSegment) : 'open'
}

/**
 * Задание коллеги по группе: закреплено за другим исполнителем. Такие задания исполнитель видит (все заявки своей группы),
 * но они не считаются его работой — не попадают в «К выполнению», сводку и ближайшее задание.
 */
export function isForeignWorkOrder(order: Pick<WorkOrder, 'assignee'>, userId?: string | null): boolean {
  return Boolean(userId && order.assignee && order.assignee.id !== userId)
}

export function matchesSegment(order: Pick<WorkOrder, 'status' | 'assignee' | 'canClaim'>, segment: MyWorkSegment, userId?: string | null): boolean {
  const pool = isPoolWorkOrder(order)
  const foreign = isForeignWorkOrder(order, userId)
  switch (segment) {
    case 'open': return canStartWorkOrder(order.status) && !pool && !foreign
    case 'pool': return pool
    case 'in_progress': return isWorkOrderInProgress(order.status) && !foreign
    case 'done': return isWorkOrderFinished(order.status) && !foreign
    case 'colleagues': return foreign
    default: return true
  }
}

/**
 * Короткое обозначение системы оборудования: код из справочника (АПС, СОУЭ, ИТП …), а без него — сокращение
 * в скобках из названия системы, если оно короткое («Система контроля и управления доступом (СКУД)» → СКУД).
 */
export function systemCode(order: Pick<WorkOrder, 'equipment'>): string | null {
  const system = order.equipment?.system
  if (!system) return null
  const code = system.code?.trim()
  if (code) return code
  const match = /\(([^()]{2,12})\)\s*;?\s*$/.exec(system.name.trim())
  return match && !/\s/.test(match[1]) ? match[1] : null
}

/** Заголовок задания с системой после вида ТО: «ТО-2 — Монитор…» → «ТО-2 · АПС — Монитор…». */
export function displayTitle(order: Pick<WorkOrder, 'title' | 'equipment' | 'maintenanceType'>): string {
  const code = systemCode(order)
  if (!code) return order.title
  const typeCode = order.maintenanceType?.code?.trim()
  if (typeCode && order.title.startsWith(`${typeCode} — `)) return `${typeCode} · ${code} — ${order.title.slice(typeCode.length + 3)}`
  if (typeCode && order.title.startsWith(`${typeCode} `)) return `${typeCode} · ${code} ${order.title.slice(typeCode.length + 1)}`
  return `${code} · ${order.title}`
}

export function matchesWorkOrderSearch(order: WorkOrder, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase('ru')
  if (!needle) return true
  return [
    order.number,
    order.title,
    order.equipment?.name,
    order.equipment?.system?.code,
    order.equipment?.system?.name,
    order.equipment?.code,
    order.equipment?.externalKey,
    order.object?.name,
    order.maintenanceType?.name,
    order.maintenanceType?.code,
    order.program?.name,
  ].some((value) => value?.toLocaleLowerCase('ru').includes(needle))
}

export function sortWorkOrders(orders: WorkOrder[]): WorkOrder[] {
  return [...orders].sort((a, b) => a.plannedAt.localeCompare(b.plannedAt) || a.number.localeCompare(b.number, 'ru'))
}

export interface WorkOrderCounters {
  toDo: number
  pool: number
  inProgress: number
  rework: number
  done: number
  /** Задания группы, закреплённые за коллегами. */
  colleagues: number
}

export function countWorkOrders(orders: WorkOrder[], userId?: string | null): WorkOrderCounters {
  return orders.reduce<WorkOrderCounters>((counters, order) => {
    if (isForeignWorkOrder(order, userId)) counters.colleagues += 1
    else if (order.status === 'IN_PROGRESS') counters.inProgress += 1
    else if (order.status === 'REJECTED') counters.rework += 1
    else if (order.status === 'COMPLETED') counters.done += 1
    else if (isPoolWorkOrder(order)) counters.pool += 1
    else if (canStartWorkOrder(order.status)) counters.toDo += 1
    return counters
  }, { toDo: 0, pool: 0, inProgress: 0, rework: 0, done: 0, colleagues: 0 })
}

/** Ближайшее задание: сначала начатое, затем просроченное, затем возвращённое, затем самое раннее. Свободные задания не учитываются. */
export function nextWorkOrder(orders: WorkOrder[], userId?: string | null): WorkOrder | null {
  const open = sortWorkOrders(orders).filter((order) => !isWorkOrderFinished(order.status) && !isPoolWorkOrder(order) && !isForeignWorkOrder(order, userId))
  return open.find((order) => order.status === 'IN_PROGRESS')
    ?? open.find((order) => order.status === 'OVERDUE')
    ?? open.find((order) => order.status === 'REJECTED')
    ?? open[0]
    ?? null
}

export interface WorkOrderDayGroup {
  key: string
  label: string
  items: WorkOrder[]
}

export function groupWorkOrdersByDay(orders: WorkOrder[], now: Date): WorkOrderDayGroup[] {
  const groups = new Map<string, WorkOrderDayGroup>()
  for (const order of sortWorkOrders(orders)) {
    const key = dayKey(order.plannedAt) || 'unknown'
    const group = groups.get(key) ?? { key, label: dayLabel(order.plannedAt, now), items: [] }
    group.items.push(order)
    groups.set(key, group)
  }
  return [...groups.values()]
}

export interface QueuedListOverride {
  status: WorkOrderStatus
  /** Взятие в работу: задание закрепляется за текущим сотрудником ещё до отправки. */
  claimed: boolean
}

/** Состояния заданий, изменённые действиями, которые ещё ждут отправки (взятие, старт, завершение). */
export function queuedStatusOverrides(queue: OutboxRecord[]): Map<string, QueuedListOverride> {
  const overrides = new Map<string, QueuedListOverride>()
  for (const record of [...queue].sort((a, b) => a.createdAt - b.createdAt)) {
    if (record.method !== 'POST' || record.state === 'failed' || record.state === 'conflict') continue
    const match = /^\/work-orders\/([^/]+)\/(claim|start|complete)$/.exec(record.path)
    if (!match) continue
    const previous = overrides.get(match[1])
    const claimed = Boolean(previous?.claimed) || match[2] !== 'complete'
    overrides.set(match[1], { status: match[2] === 'claim' ? 'ASSIGNED' : match[2] === 'start' ? 'IN_PROGRESS' : 'COMPLETED', claimed })
  }
  return overrides
}

export function applyQueuedStatus(order: WorkOrder, overrides: Map<string, QueuedListOverride>, actor?: UserRef | null): WorkOrder {
  const override = overrides.get(order.id)
  if (!override || isWorkOrderFinished(order.status)) return order
  let next = order
  if (override.claimed && !order.assignee) next = { ...next, assignee: actor ?? null, canClaim: false }
  if (order.status !== override.status && !(override.status === 'ASSIGNED' && order.status === 'OVERDUE')) next = { ...next, status: override.status, rawStatus: override.status }
  return next
}

/* ---------- Чек-лист ---------- */

export function isNumericType(type: ChecklistResultType): boolean {
  return type === 'NUMBER' || type === 'MEASUREMENT'
}

export function isChoiceType(type: ChecklistResultType): boolean {
  return type === 'CHECK' || type === 'PASS_FAIL' || type === 'PHOTO'
}

export function choiceLabels(type: ChecklistResultType): { normal: string; problem: string } {
  return type === 'CHECK' ? { normal: 'Выполнено', problem: 'Не выполнено' } : { normal: 'Норма', problem: 'Не норма' }
}

export function parseDecimal(value?: string | number | null): number | null {
  if (value === null || value === undefined) return null
  const text = String(value).replace(',', '.').replace(/\s+/g, '').trim()
  if (!text) return null
  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}

/** Приводит введённое число к формату сервера (точка, до 6 знаков после запятой). */
export function normalizeDecimalInput(value?: string | null): { value: string | null; valid: boolean } {
  if (value === null || value === undefined) return { value: null, valid: true }
  let text = value.replace(',', '.').replace(/\s+/g, '').trim()
  if (!text) return { value: null, valid: true }
  if (text.startsWith('.')) text = `0${text}`
  if (text.startsWith('-.')) text = `-0${text.slice(1)}`
  if (text.endsWith('.')) text = text.slice(0, -1)
  return /^-?\d{1,12}(\.\d{1,6})?$/.test(text) ? { value: text, valid: true } : { value: null, valid: false }
}

/** Сервер хранит числа с шестью знаками после запятой; в поле ввода показываем без лишних нулей. */
export function displayDecimal(value?: string | null): string {
  if (value === null || value === undefined) return ''
  if (/^-?\d+\.\d{6}$/.test(value)) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? String(parsed) : value
  }
  return value
}

export function isOutOfRange(value: number | null, min?: string | null, max?: string | null): boolean {
  if (value === null) return false
  const low = parseDecimal(min)
  const high = parseDecimal(max)
  return (low !== null && value < low) || (high !== null && value > high)
}

export function multiSelectValues(valueText?: string | null): string[] {
  return valueText ? valueText.split('|').map((value) => value.trim()).filter(Boolean) : []
}

export function normHint(item: Pick<ChecklistResult, 'minValue' | 'maxValue' | 'unit'>): string | null {
  const unit = item.unit ? ` ${item.unit}` : ''
  const min = parseDecimal(item.minValue)
  const max = parseDecimal(item.maxValue)
  if (min !== null && max !== null) return `Норма: ${formatNumber(min, 6)}–${formatNumber(max, 6)}${unit}`
  if (min !== null) return `Норма: не менее ${formatNumber(min, 6)}${unit}`
  if (max !== null) return `Норма: не более ${formatNumber(max, 6)}${unit}`
  return null
}

export function resultValueLabel(item: ChecklistResult): string | null {
  const type = item.resultType
  if (isNumericType(type)) {
    if (item.valueNumeric === null || item.valueNumeric === undefined || item.valueNumeric === '') return null
    return `${formatNumber(displayDecimal(item.valueNumeric), 6)}${item.unit ? ` ${item.unit}` : ''}`
  }
  if (isChoiceType(type)) return null
  if (type === 'DATE') return item.valueText ? formatDate(item.valueText) : null
  if (type === 'MULTISELECT') return multiSelectValues(item.valueText).join(', ') || null
  return item.valueText || null
}

/**
 * Полный набор полей пункта для POST /results: сервер перезаписывает ответ, значение и
 * комментарий целиком, поэтому отправлять только изменённое поле нельзя.
 */
export function inputFromResult(item: ChecklistResult): ChecklistResultInput {
  const type = item.resultType
  const input: ChecklistResultInput = { id: item.id, position: item.position, comment: item.comment ?? null }
  if (isChoiceType(type)) {
    input.answer = item.answer ?? null
  } else {
    // Для числовых и текстовых пунктов ответ вычисляет сервер по значению; явно передаём только пропуск.
    input.answer = item.answer === 'SKIPPED' ? 'SKIPPED' : null
    if (isNumericType(type)) input.value = item.valueNumeric ?? null
    else if (type === 'MULTISELECT') input.value = multiSelectValues(item.valueText)
    else input.value = item.valueText ?? null
  }
  return input
}

/** Оптимистично применяет ответ к пункту по тем же правилам, что и сервер. */
export function applyChecklistInput(item: ChecklistResult, input: ChecklistResultInput, actor: UserRef | null | undefined, at: string): ChecklistResult {
  const type = item.resultType
  let answer: ChecklistAnswer | null = input.answer === undefined ? item.answer ?? null : input.answer
  let valueText = item.valueText ?? null
  let valueNumeric = item.valueNumeric ?? null

  if (input.value !== undefined) {
    const raw = input.value
    valueText = null
    valueNumeric = null
    if (isNumericType(type)) {
      const text = raw === null || Array.isArray(raw) ? '' : String(raw).trim()
      valueNumeric = text === '' ? null : text
    } else if (type === 'MULTISELECT') {
      const values = (Array.isArray(raw) ? raw : multiSelectValues(raw)).map((value) => value.trim()).filter(Boolean)
      valueText = values.length ? values.join('|') : null
    } else if (!isChoiceType(type)) {
      const text = raw === null || Array.isArray(raw) ? '' : String(raw)
      valueText = text.trim() === '' ? null : text
    }
  }

  let outOfRange = false
  if (isNumericType(type)) {
    if (answer === 'SKIPPED') valueNumeric = null
    const numeric = parseDecimal(valueNumeric)
    outOfRange = isOutOfRange(numeric, item.minValue, item.maxValue)
    if (numeric !== null) answer = outOfRange ? 'NOT_NORMAL' : 'NORMAL'
    else if (answer !== 'SKIPPED') answer = null
  } else if (!isChoiceType(type)) {
    if (answer !== 'SKIPPED') answer = valueText ? 'NORMAL' : null
  }

  const comment = input.comment === undefined ? item.comment ?? null : input.comment === null || input.comment === '' ? null : input.comment
  const answered = isChoiceType(type)
    ? answer !== null
    : isNumericType(type)
      ? parseDecimal(valueNumeric) !== null || answer === 'SKIPPED'
      : Boolean(valueText) || answer === 'SKIPPED'
  const changed = answer !== (item.answer ?? null)
    || valueText !== (item.valueText ?? null)
    || valueNumeric !== (item.valueNumeric ?? null)
    || comment !== (item.comment ?? null)
  if (!changed) return item
  return { ...item, answer, valueText, valueNumeric, comment, outOfRange, answered, answeredAt: at, answeredBy: actor ?? item.answeredBy ?? null }
}

export interface TimedInput {
  input: ChecklistResultInput
  at: string
}

export function recountDetail(detail: WorkOrderDetail): WorkOrderDetail {
  const photoCounts = new Map<string, number>()
  for (const photo of detail.photos) {
    if (photo.checklistResultId) photoCounts.set(photo.checklistResultId, (photoCounts.get(photo.checklistResultId) ?? 0) + 1)
  }
  const checklist = detail.checklist.map((item) => {
    const count = photoCounts.get(item.id) ?? 0
    return item.photoCount === count ? item : { ...item, photoCount: count }
  })
  return {
    ...detail,
    checklist,
    checklistCount: checklist.length,
    answeredCount: checklist.filter((item) => item.answered).length,
    hasDeviations: checklist.some((item) => item.answer === 'NOT_NORMAL'),
    photoCount: detail.photos.length,
    defectCount: detail.defects.length,
  }
}

export function applyChecklistInputs(detail: WorkOrderDetail, inputs: TimedInput[], actor: UserRef | null | undefined): WorkOrderDetail {
  if (!inputs.length) return detail
  let checklist = detail.checklist
  let changed = false
  for (const { input, at } of inputs) {
    const index = checklist.findIndex((item) => (input.id ? item.id === input.id : item.position === input.position))
    if (index === -1) continue
    const next = applyChecklistInput(checklist[index], input, actor, at)
    if (next === checklist[index]) continue
    if (!changed) {
      checklist = [...checklist]
      changed = true
    }
    checklist[index] = next
  }
  return changed ? recountDetail({ ...detail, checklist }) : detail
}

/* ---------- Действия, ожидающие отправки ---------- */

export interface PendingPhoto {
  id: string
  queueId: string
  resultId: string | null
  caption: string | null
  fileName: string
  blob?: Blob
  state: OutboxState
  createdAt: number
}

export interface PendingDefect {
  id: string
  queueId: string
  resultId: string | null
  title: string
  description: string | null
  priority: DefectPriority
  dueDate: string | null
  state: OutboxState
  createdAt: number
}

export interface PendingMeasurement {
  id: string
  queueId: string
  title: string
  unit: string | null
  minValue: string | null
  maxValue: string | null
  value: string | null
  comment: string | null
  state: OutboxState
  createdAt: number
}

export interface QueuedWorkOrderChanges {
  /** Взятие свободного задания в работу ждёт отправки. */
  claimed: boolean
  started: boolean
  startedAt: number
  completed: boolean
  completedAt: number
  completeComment: string | null
  results: TimedInput[]
  resultIds: Set<string>
  photos: PendingPhoto[]
  deletedPhotoIds: Set<string>
  defects: PendingDefect[]
  measurements: PendingMeasurement[]
  /** Записи по заданию, которые не удалось отправить (ошибка или конфликт). */
  attention: number
}

export function emptyQueuedChanges(): QueuedWorkOrderChanges {
  return { claimed: false, started: false, startedAt: 0, completed: false, completedAt: 0, completeComment: null, results: [], resultIds: new Set(), photos: [], deletedPhotoIds: new Set(), defects: [], measurements: [], attention: 0 }
}

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null)

export function queuedWorkOrderChanges(workOrderId: string, queue: OutboxRecord[]): QueuedWorkOrderChanges {
  const changes = emptyQueuedChanges()
  if (!workOrderId) return changes
  const base = `/work-orders/${workOrderId}`
  for (const record of [...queue].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))) {
    if (record.path !== base && !record.path.startsWith(`${base}/`)) continue
    if (record.state === 'failed' || record.state === 'conflict') {
      changes.attention += 1
      continue
    }
    const rest = record.path.slice(base.length)
    const body = record.jsonBody && typeof record.jsonBody === 'object' && !Array.isArray(record.jsonBody) ? (record.jsonBody as Record<string, unknown>) : {}
    if (record.method === 'POST' && rest === '/claim') {
      changes.claimed = true
    } else if (record.method === 'POST' && rest === '/start') {
      changes.claimed = true
      changes.started = true
      if (!changes.startedAt) changes.startedAt = record.createdAt
    } else if (record.method === 'POST' && rest === '/complete') {
      changes.completed = true
      changes.completedAt = record.createdAt
      changes.completeComment = text(body.comment)
    } else if (record.method === 'POST' && rest === '/results') {
      const items = Array.isArray(body.items) ? body.items : []
      for (const item of items) {
        if (!item || typeof item !== 'object') continue
        const input = item as ChecklistResultInput
        changes.results.push({ input, at: new Date(record.createdAt).toISOString() })
        if (input.id) changes.resultIds.add(input.id)
      }
    } else if (record.method === 'POST' && rest === '/photos' && record.bodyType === 'multipart') {
      changes.photos.push({
        id: `pending:${record.id}`,
        queueId: record.id,
        resultId: record.fields?.resultId || null,
        caption: record.fields?.caption || null,
        fileName: record.fileName || 'Фотография',
        blob: record.fileBlob,
        state: record.state,
        createdAt: record.createdAt,
      })
    } else if (record.method === 'DELETE' && rest.startsWith('/photos/')) {
      changes.deletedPhotoIds.add(rest.slice('/photos/'.length))
    } else if (record.method === 'POST' && rest === '/defects') {
      changes.defects.push({
        id: `pending:${record.id}`,
        queueId: record.id,
        resultId: text(body.resultId),
        title: text(body.title) ?? 'Дефект',
        description: text(body.description),
        priority: text(body.priority) ?? 'NORMAL',
        dueDate: text(body.dueDate),
        state: record.state,
        createdAt: record.createdAt,
      })
    } else if (record.method === 'POST' && rest === '/measurements') {
      changes.measurements.push({
        id: `pending:${record.id}`,
        queueId: record.id,
        title: text(body.title) ?? 'Измерение',
        unit: text(body.unit),
        minValue: text(body.minValue),
        maxValue: text(body.maxValue),
        value: text(body.value),
        comment: text(body.comment),
        state: record.state,
        createdAt: record.createdAt,
      })
    }
  }
  return changes
}

export function hasQueuedChanges(changes: QueuedWorkOrderChanges): boolean {
  return changes.claimed || changes.started || changes.completed || changes.results.length > 0 || changes.photos.length > 0
    || changes.deletedPhotoIds.size > 0 || changes.defects.length > 0 || changes.measurements.length > 0
}

export function isPendingId(id: string): boolean {
  return id.startsWith('pending:')
}

/**
 * Накладывает на серверную карточку действия из очереди, чтобы интерфейс показывал их сразу.
 * В режиме `persistable` применяются только начало работы и ответы — то, что безопасно хранить в кэше.
 */
export function applyQueuedChanges(detail: WorkOrderDetail, changes: QueuedWorkOrderChanges, actor: UserRef | null | undefined, options: { persistable?: boolean } = {}): WorkOrderDetail {
  let next = detail
  if (changes.claimed && !next.assignee && isPoolWorkOrder(next)) {
    next = { ...next, status: next.status === 'OVERDUE' ? 'OVERDUE' : 'ASSIGNED', rawStatus: 'ASSIGNED', assignee: actor ?? null, canClaim: false }
  }
  if (changes.started && canStartWorkOrder(next.status)) {
    next = { ...next, status: 'IN_PROGRESS', rawStatus: 'IN_PROGRESS', startedAt: next.startedAt ?? new Date(changes.startedAt).toISOString(), startedBy: actor ?? next.startedBy ?? null }
  }
  if (changes.results.length) next = applyChecklistInputs(next, changes.results, actor)
  if (options.persistable) return next

  if (changes.deletedPhotoIds.size) {
    next = recountDetail({ ...next, photos: next.photos.filter((photo) => !changes.deletedPhotoIds.has(photo.id)) })
  }
  if (changes.photos.length) {
    // Как и сервер (markPhotoAttached), считаем пункт «Фотография» отвеченным, когда к нему приложено фото.
    const checklist = next.checklist.map((item) => {
      if (item.resultType !== 'PHOTO' || item.answer) return item
      const photo = changes.photos.find((candidate) => candidate.resultId === item.id)
      return photo ? applyChecklistInput(item, { answer: 'NORMAL' }, actor, new Date(photo.createdAt).toISOString()) : item
    })
    if (checklist.some((item, index) => item !== next.checklist[index])) next = recountDetail({ ...next, checklist })
  }
  if (changes.measurements.length) {
    const existing = new Set(next.checklist.map((item) => item.id))
    const additions = changes.measurements.filter((measurement) => !existing.has(measurement.id))
    if (additions.length) {
      let position = next.checklist.reduce((max, item) => Math.max(max, item.position), 0)
      const items = additions.map((measurement): ChecklistResult => {
        position += 1
        const numeric = parseDecimal(measurement.value)
        const outOfRange = isOutOfRange(numeric, measurement.minValue, measurement.maxValue)
        return {
          id: measurement.id,
          templateItemId: null,
          position,
          title: measurement.title,
          description: null,
          resultType: 'MEASUREMENT',
          required: false,
          unit: measurement.unit,
          minValue: measurement.minValue,
          maxValue: measurement.maxValue,
          photoRequired: false,
          options: null,
          defectRule: 'ON_FAIL',
          adHoc: true,
          answer: numeric === null ? null : outOfRange ? 'NOT_NORMAL' : 'NORMAL',
          valueText: null,
          valueNumeric: measurement.value,
          comment: measurement.comment,
          answeredAt: numeric === null ? null : new Date(measurement.createdAt).toISOString(),
          answeredBy: numeric === null ? null : actor ?? null,
          answered: numeric !== null,
          outOfRange,
          photoCount: 0,
        }
      })
      next = recountDetail({ ...next, checklist: [...next.checklist, ...items] })
    }
  }
  if (changes.completed && !isWorkOrderFinished(next.status)) {
    next = {
      ...next,
      status: 'COMPLETED',
      rawStatus: 'COMPLETED',
      completedAt: new Date(changes.completedAt).toISOString(),
      completedBy: actor ?? null,
      executorComment: changes.completeComment ?? next.executorComment ?? null,
      resultStatus: next.checklist.some((item) => item.answer === 'NOT_NORMAL') ? 'DEVIATIONS' : 'NORMAL',
    }
  }
  return next
}

/* ---------- Готовность к завершению ---------- */

/** Локальная копия правил сервера (WorkOrderService::completionIssues) с учётом фото и дефектов из очереди. */
export function completionIssuesFor(detail: WorkOrderDetail, pendingPhotos: PendingPhoto[] = [], pendingDefects: PendingDefect[] = []): CompletionIssue[] {
  const availablePendingPhotos = pendingPhotos.filter((photo) => photo.state !== 'failed' && photo.state !== 'conflict')
  const photos = new Map<string, number>()
  for (const photo of detail.photos) {
    if (photo.checklistResultId) photos.set(photo.checklistResultId, (photos.get(photo.checklistResultId) ?? 0) + 1)
  }
  for (const photo of availablePendingPhotos) {
    if (photo.resultId) photos.set(photo.resultId, (photos.get(photo.resultId) ?? 0) + 1)
  }
  const defects = new Set<string>()
  for (const defect of detail.defects) {
    if (defect.sourceResult?.id && defect.status !== 'CANCELLED') defects.add(defect.sourceResult.id)
  }
  for (const defect of pendingDefects) {
    if (defect.resultId) defects.add(defect.resultId)
  }

  // Answers/photos can resolve item issues locally; missing programs require an engineer's server-side change.
  const issues: CompletionIssue[] = (detail.completionIssues ?? []).filter((issue) => !issue.resultId && issue.code !== 'WORK_ORDER_PHOTO_REQUIRED')
  if (detail.photos.length + availablePendingPhotos.length === 0) {
    issues.push({ code: 'WORK_ORDER_PHOTO_REQUIRED', message: 'Для завершения работы добавьте хотя бы одну фотографию к заданию или пункту чек-листа.', resultId: null, position: null })
  }
  for (const item of detail.checklist) {
    const photoCount = photos.get(item.id) ?? 0
    const push = (code: CompletionIssue['code'], message: string) => issues.push({ code, message, resultId: item.id, position: item.position })
    const comment = (item.comment ?? '').trim()
    if (item.required && (!item.answered || item.answer === 'SKIPPED')) push('REQUIRED_ITEM_MISSING', `Заполните обязательный пункт ${item.position} «${item.title}».`)
    if ((item.photoRequired || item.resultType === 'PHOTO') && photoCount === 0 && (item.required || item.answer !== 'SKIPPED')) push('PHOTO_REQUIRED', `Для пункта ${item.position} нужна фотография.`)
    if (item.answer === 'NOT_NORMAL') {
      if (!comment) push('DEVIATION_COMMENT_REQUIRED', `Пункт ${item.position} «Не норма»: опишите отклонение.`)
      if (photoCount === 0) push('DEVIATION_PHOTO_REQUIRED', `Пункт ${item.position} «Не норма»: добавьте фотографию отклонения.`)
      if (item.defectRule === 'REQUIRE' && !defects.has(item.id)) push('DEFECT_REQUIRED', `Пункт ${item.position} «Не норма»: по правилу программы нужно создать дефект.`)
    }
  }
  return issues
}

export function issueHint(code: string): string {
  switch (code) {
    case 'REQUIRED_ITEM_MISSING': return 'Нужен ответ'
    case 'SKIP_REASON_REQUIRED': return 'Укажите причину пропуска в комментарии'
    case 'PHOTO_REQUIRED': return 'Нужна фотография'
    case 'WORK_ORDER_PHOTO_REQUIRED': return 'Добавьте хотя бы одну фотографию к заданию'
    case 'DEVIATION_COMMENT_REQUIRED': return 'Опишите отклонение в комментарии'
    case 'DEVIATION_PHOTO_REQUIRED': return 'Добавьте фотографию отклонения'
    case 'DEFECT_REQUIRED': return 'Зарегистрируйте дефект'
    case 'PROGRAM_REQUIRED': return 'Программа ТО не назначена — сообщите менеджеру, чек-лист появится после назначения'
    case 'PROGRAM_CHECKLIST_EMPTY': return 'В программе ТО нет пунктов — сообщите инженеру'
    default: return 'Проверьте пункт'
  }
}

export function defectForResult(detail: WorkOrderDetail, resultId: string): Defect | null {
  return detail.defects.find((defect) => defect.sourceResult?.id === resultId && defect.status !== 'CANCELLED') ?? null
}

export function pendingDefectForResult(pending: PendingDefect[], resultId: string): PendingDefect | null {
  return pending.find((defect) => defect.resultId === resultId) ?? null
}

/* ---------- Фотографии ---------- */

export function validatePhotoFile(file: File): string | null {
  const type = file.type.toLowerCase()
  if (type === 'image/heic' || type === 'image/heif') return 'Формат HEIC не поддерживается. Выберите JPEG, PNG или WebP'
  if (!type.startsWith('image/')) return 'Можно приложить только изображение (JPEG, PNG, WebP или GIF)'
  if (file.size > MAX_PHOTO_BYTES) return 'Фотография больше 20 МБ. Уменьшите размер и повторите'
  return null
}
